import type { LikedSnapsCountMap } from '@musinsa/services-curator';
import { deleteSnapLikedService, postSnapLikedService } from '@musinsa/services-curator';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { curatorQueries } from '../../query-factory';

export interface SnapLikeMutationVariables {
  snapId: string;
  isLiked: boolean;
}

interface SnapLikeMutationContext {
  previousMaps: Array<[readonly unknown[], LikedSnapsCountMap | undefined]>;
}

interface UseSnapLikeMutationProps {
  onSuccess?: (variables: SnapLikeMutationVariables) => void;
  onError?: (error: unknown, variables: SnapLikeMutationVariables) => void;
}

export const useSnapLikeMutation = ({ onSuccess, onError }: UseSnapLikeMutationProps = {}) => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, SnapLikeMutationVariables, SnapLikeMutationContext>({
    mutationFn: async ({ snapId, isLiked }) => {
      if (isLiked) {
        await postSnapLikedService({ snapId });
      } else {
        await deleteSnapLikedService({ snapId });
      }
    },
    meta: {
      skipGlobalErrorHandler: true,
    },
    onMutate: async ({ snapId, isLiked }) => {
      const queryKey = curatorQueries['snap-like']._def;
      await queryClient.cancelQueries({ queryKey });

      const previousMaps = queryClient.getQueriesData<LikedSnapsCountMap>({ queryKey });

      queryClient.setQueriesData<LikedSnapsCountMap>({ queryKey }, (prev) => {
        if (!prev || !(snapId in prev)) {
          return prev;
        }
        const current = prev[snapId];
        return {
          ...prev,
          [snapId]: {
            isLiked,
            likeCount: Math.max(0, current.likeCount + (isLiked ? 1 : -1)),
          },
        };
      });

      return { previousMaps };
    },
    onError: (error, variables, context) => {
      context?.previousMaps.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      onError?.(error, variables);
    },
    onSuccess: (_data, variables) => {
      onSuccess?.(variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: curatorQueries['snap-like']._def });
    },
  });
};
